import { z } from 'zod';

import { ATS_PROVIDERS } from '@kiku/integrations/types';

// ── Candidate ───────────────────────────────────────────────────────────────

export const candidateSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  phoneNumber: z.string().nullable(),
  phoneCountryCode: z.string().nullable(),
  atsExternalCandidateId: z.string().nullable(),
  atsProvider: z.enum(ATS_PROVIDERS).nullable(),
  createdAt: z.date(),
  updatedAt: z.date()
});

export type Candidate = z.infer<typeof candidateSchema>;

export const createCandidateInputSchema = z.object({
  name: z.string().min(1).max(200),
  email: z.string().email(),
  phoneNumber: z.string().optional(),
  phoneCountryCode: z.string().max(5).optional(),
  atsExternalCandidateId: z.string().optional(),
  atsProvider: z.enum(ATS_PROVIDERS).optional()
});

export type CreateCandidateInput = z.infer<typeof createCandidateInputSchema>;

export class CandidateNotFoundError extends Error {
  constructor(id: string) {
    super(`Candidate ${id} not found`);
    this.name = 'CandidateNotFoundError';
  }
}

// ── Application ─────────────────────────────────────────────────────────────

export const applicationSchema = z.object({
  id: z.string(),
  candidateId: z.string(),
  jobId: z.string(),
  stageId: z.string(),
  createdById: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  candidate: z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    phoneNumber: z.string().nullable(),
    phoneCountryCode: z.string().nullable()
  })
});

export type Application = z.infer<typeof applicationSchema>;

export const createApplicationInputSchema = z.object({
  name: z.string().min(1).max(200),
  email: z.string().email(),
  phoneNumber: z.string().optional(),
  phoneCountryCode: z.string().max(5).optional()
});

export type CreateApplicationInput = z.infer<typeof createApplicationInputSchema>;

export const updateApplicationStageInputSchema = z.object({
  stageId: z.string()
});

export type UpdateApplicationStageInput = z.infer<typeof updateApplicationStageInputSchema>;

export const listApplicationsParamsSchema = z.object({
  stageId: z.string().optional(),
  page: z
    .union([z.number(), z.string().transform(Number)])
    .pipe(z.number().int().min(1))
    .optional()
    .default(1),
  limit: z
    .union([z.number(), z.string().transform(Number)])
    .pipe(z.number().int().min(1).max(500))
    .optional()
    .default(200)
});

export type ListApplicationsParams = z.infer<typeof listApplicationsParamsSchema>;

export class ApplicationNotFoundError extends Error {
  constructor(id: string) {
    super(`Application ${id} not found`);
    this.name = 'ApplicationNotFoundError';
  }
}

export class ApplicationConflictError extends Error {
  constructor(candidateId: string, jobId: string) {
    super(`Application already exists for candidate ${candidateId} on job ${jobId}`);
    this.name = 'ApplicationConflictError';
  }
}

// ── Extension lookup ─────────────────────────────────────────────────────────

export const extensionLookupQuerySchema = z.object({
  atsName: z.string(),
  externalCandidateId: z.string().optional(),
  externalJobId: z.string().optional(),
  externalApplicationId: z.string().optional()
});

export type ExtensionLookupQuery = z.infer<typeof extensionLookupQuerySchema>;

export const extensionLookupInputSchema = z.object({
  atsProvider: z.enum(ATS_PROVIDERS),
  externalCandidateId: z.string().optional(),
  externalJobId: z.string().optional(),
  externalApplicationId: z.string().optional()
});

export type ExtensionLookupInput = z.infer<typeof extensionLookupInputSchema>;

export const extensionLookupResultSchema = z.object({
  candidateName: z.string(),
  applicationId: z.string().uuid(),
  jobId: z.string().uuid(),
  deptSlug: z.string(),
  orgSlug: z.string(),
  hasScreeningTranscript: z.boolean()
});

export type ExtensionLookupResult = z.infer<typeof extensionLookupResultSchema>;
